import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, SerializationContext, SerializedData, isSameData, isSameList } from '../../models/dto/dto.js';
import { BillingProfile } from '../../models/serializable/billing-profile.js';
import { InvoiceType } from '../../models/serializable/invoicing/invoice-type.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { Component } from '../core/component.js';
import { LegacyBillingDetailsComponent } from './legacy-billing-details-component.js';

export class BillingDetailsComponent extends Component {
  static readonly TYPE_ID: string = '18329832a0e3479d93460fa80a95f0d8';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    private readonly profiles: BillingProfile[],
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return BillingDetailsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(BillingDetailsComponent.SCHEMA_VERSION);
  }

  get size(): number {
    return this.profiles.length;
  }

  updateBillingProfile(billingProfile: BillingProfile): void {
    for(let i = 0; i < this.profiles.length; i++) {
      if(this.profiles[i].invoiceType == billingProfile.invoiceType) {
        this.profiles[i] = billingProfile;
        return;
      }
    }

    this.profiles.push(billingProfile);
  }

  getBillingProfile(invoiceType: InvoiceType | null): BillingProfile | null {
    if(invoiceType == null) return null;

    for(let i = 0; i < this.profiles.length; i++) {
      if(this.profiles[i].invoiceType == invoiceType) {
        return this.profiles[i];
      }
    }

    return null;
  }

  getAnyBillingProfile(invoiceType?: InvoiceType | null): BillingProfile | null {
    if(invoiceType != null) {
      for(let i = 0; i < this.profiles.length; i++) {
        if(this.profiles[i].invoiceType == invoiceType) {
          return this.profiles[i];
        }
      }
    }

    return this.profiles.length > 0 ? this.profiles[0] : null;
  }

  static empty(): BillingDetailsComponent {
    return new BillingDetailsComponent([]);
  }

  static fromLegacy(component: LegacyBillingDetailsComponent): BillingDetailsComponent | null {
    if(isNullOrUndefined(component.name) || isNullOrUndefined(component.email)) {
      return null;
    }

    const billingProfile = BillingProfile.personal({
      name: component.name,
      email: component.email,
      phoneNumber: component.phoneNumber?.clone() ?? null,
      address: component.address?.clone() ?? null,
    });

    return new BillingDetailsComponent([billingProfile]);
  }

  static fromProfile(billingProfile: BillingProfile): BillingDetailsComponent {
    return new BillingDetailsComponent([billingProfile]);
  }

  static fromMap(context: SerializationContext, map: SerializedData): BillingDetailsComponent {
    return new BillingDetailsComponent(
        MapUtils.getList<BillingProfile>(map, 'profiles', (v) => context.fromData<BillingProfile>(v as SerializedData))
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'profiles': this.profiles.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new BillingDetailsComponent(
        this.profiles.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.profiles, this.profiles, isSameData);
  }

  prune(invoiceType: InvoiceType): this {
    const profiles: BillingProfile[] = [];
    for(let i = 0; i < this.profiles.length; i++) {
      if(this.profiles[i].invoiceType == invoiceType) {
        profiles.push(this.profiles[i].clone());
        break;
      }
    }

    return new BillingDetailsComponent(profiles) as this;
  }
}

export class BillingDetailsComponentBuilder extends DataModelBuilder<BillingDetailsComponent> {
  build(context: SerializationContext, map: SerializedData): BillingDetailsComponent {
    return BillingDetailsComponent.fromMap(context, map);
  }
}
